import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import {
  type ComplexSearchPdpsV1WorkspacesWorkspaceIdPdpsSearchPostData,
  type ComplexSearchPdpsV1WorkspacesWorkspaceIdPdpsSearchPostError,
  type ComplexSearchPdpsV1WorkspacesWorkspaceIdPdpsSearchPostResponse,
  complexSearchPdpsV1WorkspacesWorkspaceIdPdpsSearchPost,
} from '../../generated';

export type ComplexSearchPdpsMutationVariables = ComplexSearchPdpsV1WorkspacesWorkspaceIdPdpsSearchPostData['path'] &
  ComplexSearchPdpsV1WorkspacesWorkspaceIdPdpsSearchPostData['body'];

export type ComplexSearchPdpsMutation = {
  data: ComplexSearchPdpsV1WorkspacesWorkspaceIdPdpsSearchPostResponse;
  total_count: number;
};

/**
 * Extracts the total_count from the Vizit-Total-Count response header
 */
function extractTotalCount(response: Response): number {
  const totalCountHeader = response.headers.get('vizit-total-count');
  return totalCountHeader ? Number.parseInt(totalCountHeader, 10) : 0;
}

export const useComplexSearchPdpsMutation = <
  TError = ComplexSearchPdpsV1WorkspacesWorkspaceIdPdpsSearchPostError,
  TContext = unknown,
>(
  options?: Omit<
    UseMutationOptions<ComplexSearchPdpsMutation, TError, ComplexSearchPdpsMutationVariables, TContext>,
    'mutationKey' | 'mutationFn'
  >,
) => {
  return useMutation<ComplexSearchPdpsMutation, TError, ComplexSearchPdpsMutationVariables, TContext>({
    mutationKey: ['ComplexSearchPdps'],
    mutationFn: async ({ workspace_id, ...variables }: ComplexSearchPdpsMutationVariables) => {
      const { data, response } = await complexSearchPdpsV1WorkspacesWorkspaceIdPdpsSearchPost({
        path: { workspace_id },
        body: variables,
        throwOnError: true,
      });
      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
      };
    },
    ...options,
  });
};

useComplexSearchPdpsMutation.fetcher = (
  { workspace_id, ...variables }: ComplexSearchPdpsMutationVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data, response } = await complexSearchPdpsV1WorkspacesWorkspaceIdPdpsSearchPost({
      path: { workspace_id },
      body: variables,
      throwOnError: true,
    });
    return {
      data: data ?? [],
      total_count: extractTotalCount(response),
    };
  };
};
